import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  FileText,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Search,
  Filter,
  Download,
  Eye,
  Loader,
  AlertCircle,
  Edit3,
  Calendar,
  User,
  RefreshCw,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/firebase/api';
import InvoicePreview from './InvoicePreview';
import InvoiceApproval from './InvoiceApproval';
import { InvoiceUploadDialog } from './InvoiceUploadDialog';

interface InvoiceManagementProps {
  currentUser: { id: string; name: string; role: string; companyId?: string };
}

export default function InvoiceManagement({ currentUser }: InvoiceManagementProps) {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPayment, setFilterPayment] = useState<string>('all');
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showApproval, setShowApproval] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load invoices using Firebase API
      const invoicesData = await api.fetchInvoices(currentUser.companyId || 'default');
      setInvoices(invoicesData || []);

      // Calculate statistics from invoice data
      const calculateStats = (data: any[]) => {
        const pending = data.filter(i => i.approvalStatus === 'pending').length;
        const approved = data.filter(i => i.approvalStatus === 'approved').length;
        const rejected = data.filter(i => i.approvalStatus === 'rejected').length;
        const paid = data.filter(i => i.paymentStatus === 'paid').length;
        const totalAmount = data.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

        return { pending, approved, rejected, paid, total: data.length, totalAmount };
      };

      setStats(calculateStats(invoicesData || []));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter((invoice) => {
    // Search filter
    const matchesSearch =
      searchQuery === '' ||
      invoice.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.uploadedBy?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.ticketId?.toLowerCase().includes(searchQuery.toLowerCase());

    // Status filter
    const invoiceStatus = invoice.approvalStatus || 'pending';
    const matchesStatus = filterStatus === 'all' || invoiceStatus === filterStatus;

    // Payment filter
    const paymentStatus = invoice.paymentStatus || 'unpaid';
    const matchesPayment = filterPayment === 'all' || paymentStatus === filterPayment;

    // Vendor Role Filter
    const isVendor = currentUser?.role === 'vendor';
    const matchesVendor = !isVendor || (
      (invoice.uploadedBy === currentUser.name) ||
      (invoice.vendorId === currentUser.id) ||
      (invoice.userId === currentUser.id)
    );

    return matchesSearch && matchesStatus && matchesPayment && matchesVendor;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge className="bg-green-100 text-green-800 text-xs">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800 text-xs">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      case 'revision-requested':
        return (
          <Badge className="bg-orange-100 text-orange-800 text-xs">
            <Edit3 className="h-3 w-3 mr-1" />
            Revision
          </Badge>
        );
      default:
        return (
          <Badge className="bg-yellow-100 text-yellow-800 text-xs">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const getPaymentBadge = (status: string) => {
    if (status === 'paid') {
      return (
        <Badge className="bg-blue-100 text-blue-800 text-xs">
          <DollarSign className="h-3 w-3 mr-1" />
          Paid
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-800 text-xs">
        <DollarSign className="h-3 w-3 mr-1" />
        Unpaid
      </Badge>
    );
  };

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setShowApproval(true);
  };

  const handlePreviewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setShowPreview(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-700">Loading invoice management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Invoice Management</h1>
            <p className="text-gray-600 mt-1">Manage and track all service invoices</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowUpload(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Upload Invoice
            </Button>
            <Button onClick={loadData} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Total Invoices</p>
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500 mt-1">
              ${stats.totalAmount?.toFixed(2)} total
            </p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Pending Review</p>
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <p className="text-3xl font-bold text-yellow-700">{stats.pending}</p>
            <p className="text-sm text-yellow-600 mt-1">Require attention</p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Approved</p>
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-700">{stats.approved}</p>
            <p className="text-sm text-green-600 mt-1">
              {stats.unpaid} awaiting payment
            </p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Paid</p>
              <DollarSign className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-blue-700">{stats.paid}</p>
            <p className="text-sm text-blue-600 mt-1">
              ${stats.paidAmount?.toFixed(2)} paid out
            </p>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by invoice #, provider, or ticket..."
                className="pl-10"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="revision-requested">Revision Requested</option>
          </select>

          {/* Payment Filter */}
          <select
            value={filterPayment}
            onChange={(e) => setFilterPayment(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            <option value="all">All Payment</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>

        <div className="mt-3 flex items-center justify-between text-sm">
          <p className="text-gray-600">
            Showing {filteredInvoices.length} of {invoices.length} invoices
          </p>
          {(searchQuery || filterStatus !== 'all' || filterPayment !== 'all') && (
            <Button
              onClick={() => {
                setSearchQuery('');
                setFilterStatus('all');
                setFilterPayment('all');
              }}
              variant="ghost"
              size="sm"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Invoices Found</h3>
          <p className="text-gray-600">
            {searchQuery || filterStatus !== 'all' || filterPayment !== 'all'
              ? 'Try adjusting your filters'
              : 'Invoices will appear here when service providers upload them'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => (
            <Card
              key={invoice.id}
              className="p-6 hover:shadow-lg transition-shadow border-2"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Invoice Info */}
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <FileText className="h-6 w-6 text-blue-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">
                        Invoice #{invoice.invoiceNumber}
                      </h3>
                      <p className="text-sm text-gray-600">Ticket: {invoice.ticketId}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {getStatusBadge(invoice.approvalStatus || 'pending')}
                    {getPaymentBadge(invoice.paymentStatus || 'unpaid')}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-semibold text-blue-600">
                        ${invoice.amount?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">Provider:</span>
                      <span className="font-medium text-gray-900">{invoice.uploadedBy}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">Uploaded:</span>
                      <span className="text-gray-900">
                        {new Date(invoice.uploadedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col md:flex-row gap-2">
                  <Button
                    onClick={() => handlePreviewInvoice(invoice)}
                    variant="outline"
                    size="sm"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                  <Button
                    onClick={() => handleViewInvoice(invoice)}
                    className="bg-blue-600 hover:bg-blue-700"
                    size="sm"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Manage
                  </Button>
                </div>
              </div>

              {/* Priority Alerts */}
              {invoice.approvalStatus === 'pending' && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <p className="text-sm text-yellow-800">
                      <strong>Action Required:</strong> This invoice needs your review
                    </p>
                  </div>
                </div>
              )}

              {invoice.approvalStatus === 'approved' && invoice.paymentStatus !== 'paid' && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <p className="text-sm text-green-800">
                      <strong>Ready for Payment:</strong> Approved and awaiting payment processing
                    </p>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && selectedInvoice && (
        <InvoicePreview
          invoice={selectedInvoice}
          onClose={() => {
            setShowPreview(false);
            setSelectedInvoice(null);
          }}
        />
      )}

      {/* Approval Modal */}
      {showApproval && selectedInvoice && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-semibold text-gray-900">Invoice Management</h3>
              <Button
                onClick={() => {
                  setShowApproval(false);
                  setSelectedInvoice(null);
                }}
                variant="ghost"
                size="sm"
              >
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-6">
              <InvoiceApproval
                invoice={selectedInvoice}
                currentUser={currentUser}
                onUpdate={() => {
                  loadData();
                }}
                onPreview={() => {
                  setShowApproval(false);
                  setShowPreview(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <InvoiceUploadDialog
        open={showUpload}
        onClose={() => setShowUpload(false)}
        currentUser={currentUser}
        onSuccess={loadData}
      />
    </div>
  );
}
